import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, MapPin, Clock, Shield, CheckCircle2, XCircle } from "lucide-react";
import { SCHEDULE, TEAMS } from "../data/tournament";
import { api } from "../lib/api";
import { resolveGameTeams, computeStandings } from "../lib/standings";
import LiveBadge from "../components/LiveBadge";

const findTeamId = (name) => TEAMS.find((t) => t.name === name)?.id || null;

const Match = () => {
  const { num } = useParams();
  const gameNum = parseInt(num, 10);
  const game = SCHEDULE.find((g) => g.num === gameNum);

  const [scores, setScores] = useState([]);
  const [awayRoster, setAwayRoster] = useState([]);
  const [homeRoster, setHomeRoster] = useState([]);
  const [awayTeamInfo, setAwayTeamInfo] = useState(null);
  const [homeTeamInfo, setHomeTeamInfo] = useState(null);
  const [attendance, setAttendance] = useState([]);
  const [gameStats, setGameStats] = useState([]);
  const [statsPublic, setStatsPublic] = useState(false);
  const [loading, setLoading] = useState(true);

  // Load settings (stats visibility flag)
  useEffect(() => {
    api.get("/settings").then((r) => setStatsPublic(!!r.data.stats_public)).catch(() => setStatsPublic(false));
  }, []);

  // Load scores for placeholder resolution
  useEffect(() => {
    api.get("/scores").then((r) => setScores(r.data)).catch(() => setScores([]));
  }, []);

  const scoreByGame = useMemo(() => {
    const m = {};
    scores.forEach((s) => (m[s.game_num] = s));
    return m;
  }, [scores]);

  const standingsByCat = useMemo(() => {
    const out = {};
    ["2017", "2018"].forEach((cat) => {
      const prelim = SCHEDULE.filter((g) => g.category === cat && !g.phase);
      const allDone =
        prelim.length > 0 && prelim.every((g) => scoreByGame[g.num]?.status === "final");
      if (!allDone) return;
      const teams = TEAMS.filter((t) => t.category === cat).map((t) => t.name);
      out[cat] = computeStandings(scoreByGame, teams);
    });
    return out;
  }, [scoreByGame]);

  const resolved = useMemo(() => {
    if (!game) return null;
    return resolveGameTeams(game, scoreByGame, standingsByCat);
  }, [game, scoreByGame, standingsByCat]);

  const awayTeamId = resolved?.awayResolved ? findTeamId(resolved.away) : null;
  const homeTeamId = resolved?.homeResolved ? findTeamId(resolved.home) : null;

  // Load rosters + team info + attendance
  useEffect(() => {
    let cancel = false;
    const loadAll = async () => {
      setLoading(true);
      const tasks = [
        api.get(`/games/${gameNum}/attendance`).then((r) => r.data).catch(() => []),
        api.get(`/games/${gameNum}/stats`).then((r) => r.data).catch(() => []),
      ];
      if (awayTeamId) {
        tasks.push(api.get(`/teams/${awayTeamId}/roster`).then((r) => r.data).catch(() => []));
        tasks.push(api.get(`/teams/${awayTeamId}`).then((r) => r.data).catch(() => null));
      }
      if (homeTeamId) {
        tasks.push(api.get(`/teams/${homeTeamId}/roster`).then((r) => r.data).catch(() => []));
        tasks.push(api.get(`/teams/${homeTeamId}`).then((r) => r.data).catch(() => null));
      }
      const results = await Promise.all(tasks);
      if (cancel) return;
      setAttendance(results[0] || []);
      setGameStats(results[1] || []);
      let idx = 2;
      if (awayTeamId) {
        setAwayRoster(results[idx++] || []);
        setAwayTeamInfo(results[idx++] || null);
      } else {
        setAwayRoster([]);
        setAwayTeamInfo(null);
      }
      if (homeTeamId) {
        setHomeRoster(results[idx++] || []);
        setHomeTeamInfo(results[idx++] || null);
      } else {
        setHomeRoster([]);
        setHomeTeamInfo(null);
      }
      setLoading(false);
    };
    loadAll();
    return () => {
      cancel = true;
    };
  }, [gameNum, awayTeamId, homeTeamId]);

  const presenceMap = useMemo(() => {
    const m = {};
    attendance.forEach((a) => {
      m[`${a.team_id}:${a.player_id}`] = a.present;
    });
    return m;
  }, [attendance]);

  const statsMap = useMemo(() => {
    if (!statsPublic) return {};
    const m = {};
    gameStats.forEach((s) => {
      m[`${s.team_id}:${s.player_id}`] = s;
    });
    return m;
  }, [gameStats, statsPublic]);

  const score = scoreByGame[gameNum];

  if (!game) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] text-white flex items-center justify-center px-6">
        <div className="text-center">
          <h1 className="font-display text-4xl uppercase text-[#E60012]">Match introuvable</h1>
          <p className="mt-4 text-white/60">La partie #{num} n'existe pas à l'horaire.</p>
          <Link to="/horaire" className="inline-flex items-center gap-2 mt-6 px-5 py-2.5 bg-[#E60012] font-condensed uppercase tracking-[0.2em] text-xs clip-tab">
            <ArrowLeft className="w-4 h-4" /> Retour à l'horaire
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#0a0a0a] text-white min-h-screen" data-testid="match-page">
      {/* HERO */}
      <section className="relative border-b border-white/10 overflow-hidden">
        <div className="absolute inset-0 diagonal-stripes opacity-30" />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 py-10 sm:py-14">
          <Link
            to="/horaire"
            className="inline-flex items-center gap-2 text-white/60 hover:text-white font-condensed uppercase tracking-[0.2em] text-xs mb-6"
            data-testid="match-back-link"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Horaire
          </Link>
          <div className="flex items-start justify-between flex-wrap gap-6">
            <div>
              <div className="flex items-center gap-3 text-white/60 text-xs font-condensed uppercase tracking-[0.3em]">
                <span className="text-[#E60012] font-display text-3xl leading-none">#{game.num}</span>
                <span>{game.day}</span>
                <span className="w-1 h-1 bg-[#E60012] rounded-full" />
                <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {game.time}</span>
                <span className="w-1 h-1 bg-[#E60012] rounded-full" />
                <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {game.ice}</span>
              </div>
              {game.phase && (
                <div className="mt-2 inline-block px-2 py-1 bg-[#E60012]/20 border border-[#E60012]/50 text-[#E60012] text-[10px] uppercase tracking-[0.25em] font-condensed">
                  {game.phase}
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              <span className="px-3 py-1 border border-white/20 text-white/70 text-xs font-condensed uppercase tracking-[0.2em]">
                Catégorie {game.category}
              </span>
            </div>
          </div>

          {/* Teams + Score */}
          <div className="mt-10 grid grid-cols-[1fr_auto_1fr] items-center gap-4 sm:gap-8">
            <TeamHero name={resolved?.away} resolved={resolved?.awayResolved} info={awayTeamInfo} align="right" placeholder={game.away} />
            <ScoreBig score={score} />
            <TeamHero name={resolved?.home} resolved={resolved?.homeResolved} info={homeTeamInfo} align="left" placeholder={game.home} />
          </div>
        </div>
      </section>

      {/* ROSTERS */}
      {resolved && (resolved.awayResolved || resolved.homeResolved) ? (
        <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-14">
          <div className="flex items-center gap-3 mb-8">
            <Shield className="w-5 h-5 text-[#E60012]" />
            <h2 className="font-display text-3xl sm:text-4xl uppercase">Alignements</h2>
            <div className="flex-1 h-px bg-white/10" />
            <span className="text-white/50 text-xs font-condensed uppercase tracking-[0.2em]">
              Présences confirmées en temps réel
            </span>
          </div>

          {loading ? (
            <div className="text-center py-12 text-white/40 font-condensed uppercase tracking-[0.2em]">
              Chargement des alignements…
            </div>
          ) : (
            <div className="grid lg:grid-cols-2 gap-6">
              <RosterCard
                title={resolved.away}
                roster={awayRoster}
                resolved={resolved.awayResolved}
                teamId={awayTeamId}
                presenceMap={presenceMap}
                statsMap={statsMap}
                accent="bg-[#E60012]"
                info={awayTeamInfo}
                placeholder={game.away}
              />
              <RosterCard
                title={resolved.home}
                roster={homeRoster}
                resolved={resolved.homeResolved}
                teamId={homeTeamId}
                presenceMap={presenceMap}
                statsMap={statsMap}
                accent="bg-zinc-700"
                info={homeTeamInfo}
                placeholder={game.home}
              />
            </div>
          )}
        </section>
      ) : (
        <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-14">
          <div className="bg-[#111] border border-white/10 p-8 text-center">
            <p className="text-white/60 text-sm font-condensed uppercase tracking-[0.2em]">
              Les équipes de cette partie seront déterminées à la fin de la phase préliminaire.
            </p>
          </div>
        </section>
      )}
    </div>
  );
};

const TeamHero = ({ name, resolved, info, align, placeholder }) => {
  const showOrigin = resolved && placeholder && placeholder !== name;
  return (
    <div className={`flex flex-col sm:flex-row items-center gap-4 ${align === "right" ? "sm:flex-row-reverse sm:text-right" : ""}`}>
      <TeamLogo info={info} size="lg" />
      <div>
        <div className={`font-display text-2xl sm:text-4xl uppercase leading-tight ${resolved ? "text-white" : "text-white/55 italic"}`}>
          {name || "—"}
        </div>
        {showOrigin && (
          <div
            className="mt-1 text-[11px] sm:text-xs text-[#E60012]/90 font-condensed uppercase tracking-[0.25em]"
            data-testid="team-origin-label"
          >
            {placeholder}
          </div>
        )}
        {info?.motto && (
          <div className="mt-1 text-[11px] sm:text-xs text-white/50 font-condensed uppercase tracking-[0.2em]">
            « {info.motto} »
          </div>
        )}
      </div>
    </div>
  );
};

const TeamLogo = ({ info, size = "md" }) => {
  const dims = size === "lg" ? "w-20 h-20 sm:w-24 sm:h-24" : size === "sm" ? "w-6 h-6" : "w-12 h-12";
  if (info?.logo_base64) {
    return <img src={info.logo_base64} alt="" className={`${dims} object-contain shrink-0`} data-testid="team-logo" />;
  }
  return (
    <div className={`${dims} bg-black/40 border border-white/10 flex items-center justify-center shrink-0`}>
      <Shield className="w-1/2 h-1/2 text-[#E60012]/70" />
    </div>
  );
};

const ScoreBig = ({ score }) => {
  const isLive = score?.status === "live";
  const isFinal = score?.status === "final";
  const hasScore = score && score.away_score != null && score.home_score != null;
  return (
    <div className="text-center">
      {hasScore ? (
        <div>
          <div className="font-display text-5xl sm:text-7xl tracking-wider leading-none">
            <span className="text-white">{score.away_score}</span>
            <span className="text-white/30 mx-3">—</span>
            <span className="text-white">{score.home_score}</span>
          </div>
          {isLive && (
            <div className="mt-2 inline-flex items-center gap-1 px-2 py-0.5 bg-red-600 text-white text-[10px] uppercase tracking-wider font-condensed animate-pulse">
              <Radio className="w-2.5 h-2.5" /> Live
            </div>
          )}
          {isFinal && (
            <div className="mt-2 inline-block px-2 py-0.5 bg-white/10 text-white/70 text-[10px] uppercase tracking-wider font-condensed">
              Final
            </div>
          )}
        </div>
      ) : (
        <div className="font-display text-4xl sm:text-5xl text-[#E60012] leading-none">VS</div>
      )}
    </div>
  );
};

const RosterCard = ({ title, roster, resolved, teamId, presenceMap, statsMap, accent, info, placeholder }) => {
  const presentCount = roster.filter((p) => presenceMap[`${teamId}:${p.id}`] !== false).length;
  const total = roster.length;
  const hasAnyStat = roster.some((p) => {
    const s = statsMap?.[`${teamId}:${p.id}`];
    return s && (s.goals > 0 || s.assists > 0 || s.pim > 0);
  });
  const showOrigin = resolved && placeholder && placeholder !== title;

  return (
    <div className="bg-[#111] border border-white/10 overflow-hidden" data-testid={`roster-card-${teamId || "tbd"}`}>
      <div className={`${accent} px-5 py-4 flex items-center gap-3`}>
        <TeamLogo info={info} size="md" />
        <div className="flex-1 min-w-0">
          <div className={`font-display text-xl sm:text-2xl uppercase truncate ${resolved ? "text-white" : "text-white/60 italic"}`}>
            {title || "—"}
          </div>
          {showOrigin && (
            <div className="text-[10px] sm:text-xs text-white/85 font-condensed uppercase tracking-[0.2em] truncate">
              {placeholder}
            </div>
          )}
          {resolved && total > 0 && (
            <div className="text-[10px] sm:text-xs text-white/85 font-condensed uppercase tracking-[0.2em]">
              {presentCount}/{total} présents
            </div>
          )}
        </div>
      </div>

      {!resolved || !teamId ? (
        <div className="p-6 text-center text-white/40 font-condensed uppercase tracking-[0.2em] text-xs">
          Équipe à déterminer
        </div>
      ) : total === 0 ? (
        <div className="p-6 text-center text-white/40 font-condensed uppercase tracking-[0.2em] text-xs">
          Alignement à venir
        </div>
      ) : (
        <>
          {hasAnyStat && (
            <div className="grid grid-cols-[40px_1fr_32px_32px_32px_32px] gap-2 px-5 py-2 bg-black/40 text-[10px] font-condensed uppercase tracking-[0.2em] text-white/50">
              <div>#</div>
              <div>Joueur</div>
              <div className="text-center">B</div>
              <div className="text-center">A</div>
              <div className="text-center">PTS</div>
              <div className="text-center">PIM</div>
            </div>
          )}
          <ul>
            {roster.map((p, idx) => {
              const present = presenceMap[`${teamId}:${p.id}`] !== false;
              const s = statsMap?.[`${teamId}:${p.id}`] || { goals: 0, assists: 0, pim: 0 };
              const pts = (s.goals || 0) + (s.assists || 0);
              return (
                <li
                  key={p.id}
                  className={`${hasAnyStat ? "grid grid-cols-[40px_1fr_32px_32px_32px_32px]" : "flex"} gap-2 items-center px-5 py-3 border-t border-white/5 ${idx % 2 === 0 ? "bg-[#0c0c0c]" : "bg-[#141414]"}`}
                  data-testid={`roster-player-${p.id}`}
                >
                  <div className="font-display text-xl text-[#E60012] shrink-0">
                    {p.number || "—"}
                  </div>
                  <div className="flex-1 min-w-0 flex items-center gap-2">
                    <div className="flex-1 min-w-0">
                      <div className={`font-condensed truncate ${present ? "text-white" : "text-white/30 line-through"}`}>
                        {p.name}
                      </div>
                      {p.position && (
                        <div className="text-[10px] text-white/40 uppercase tracking-[0.2em] font-condensed">
                          {p.position === "G" ? "Gardien" : p.position === "D" ? "Défenseur" : p.position === "A" ? "Attaquant" : p.position}
                        </div>
                      )}
                    </div>
                    {!hasAnyStat && (
                      present ? (
                        <div className="flex items-center gap-1.5 text-emerald-400 text-[10px] font-condensed uppercase tracking-[0.2em] shrink-0" data-testid="presence-present">
                          <CheckCircle2 className="w-3.5 h-3.5" /> Présent
                        </div>
                      ) : (
                        <div className="flex items-center gap-1.5 text-white/35 text-[10px] font-condensed uppercase tracking-[0.2em] shrink-0" data-testid="presence-absent">
                          <XCircle className="w-3.5 h-3.5" /> Absent
                        </div>
                      )
                    )}
                  </div>
                  {hasAnyStat && (
                    <>
                      <div className="text-center font-display text-lg text-white">{s.goals || "—"}</div>
                      <div className="text-center font-display text-lg text-white/70">{s.assists || "—"}</div>
                      <div className="text-center font-display text-lg text-[#E60012]">{pts || "—"}</div>
                      <div className="text-center font-condensed text-sm text-white/55">{s.pim || "—"}</div>
                    </>
                  )}
                </li>
              );
            })}
          </ul>
        </>
      )}
    </div>
  );
};

export default Match;
